import { Button } from "@heroui/button";
import { Link } from "@heroui/link";
import { Avatar } from "@heroui/avatar";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/dropdown";
import {
  Navbar as HeroUINavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
} from "@heroui/navbar";
import { link as linkStyles } from "@heroui/theme";
import { ThemeProps } from "@heroui/use-theme";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { siteConfig } from "@/config/site";
import { ThemeSwitch } from "@/components/theme-switch";
import { BellIcon } from "@/components/icons";
import { Logo } from "@/components/icons";
import { slugify } from "@/utils/seo";
import { preloadRouteByPath } from "@/utils/routePrefetch";
import { useAuth } from "@/state/AuthContext";
import { useNotifications } from "@/api/useNotifications";

export const Navbar = () => {
  const { user, stopImpersonation } = useAuth();
  const canToggleTheme = Boolean(user?.admin);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  const {
    data: notifications,
    loading: notifLoading,
    markRead,
    clearAll,
    removeNotification,
  } = useNotifications(user?.id);
  const unreadCount = notifications?.filter((item) => !item.read).length ?? 0;
  const location = useLocation();
  const navigate = useNavigate();
  const redirectTarget = `${location.pathname}${location.search}`;
  const notificationDropdown = user ? (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button
          isIconOnly
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`
              : "Notifications"
          }
          className="relative overflow-visible"
          isDisabled={notifLoading}
          variant="light"
        >
          <BellIcon className="text-xl" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute right-1 top-1 flex h-4 min-w-4 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="Notifications" className="dropdown-motion">
        {notifLoading ? (
          <DropdownItem key="loading" isDisabled>
            Chargement...
          </DropdownItem>
        ) : notifications && notifications.length > 0 ? (
          <>
            <DropdownItem key="clear-all" color="danger" onPress={clearAll}>
              Tout effacer
            </DropdownItem>
            {notifications.map((notification) => (
              <DropdownItem
                key={notification._id}
                className={notification.read ? undefined : "bg-primary/10"}
                description={notification.body}
                onPress={() => {
                  const target = notification.link || "/tableau-de-bord";

                  navigate(target);
                  if (!notification.read) {
                    markRead(notification._id);
                  }
                  removeNotification(notification._id);
                }}
              >
                {notification.title}
              </DropdownItem>
            ))}
          </>
        ) : (
          <DropdownItem key="empty" isDisabled>
            Aucune notification.
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  ) : null;
  const mobileMenuItems = useMemo(() => {
    const mainItems = siteConfig.navItems.map((item) => ({
      ...item,
      color:
        item.label === "ProfLinker"
          ? ("primary" as const)
          : ("foreground" as const),
    }));
    const authItems = user
      ? [
          {
            label: "Tableau de bord",
            href: "/tableau-de-bord",
            color: "foreground" as const,
          },
          {
            label: "Déconnexion",
            href: "/deconnexion",
            color: "danger" as const,
          },
        ]
      : [
          {
            label: "Connexion",
            href: `/connexion?redirect=${encodeURIComponent(redirectTarget)}`,
            color: "foreground" as const,
          },
          {
            label: "Inscription",
            href: "/inscription",
            color: "primary" as const,
          },
        ];

    return [...mainItems, ...authItems];
  }, [redirectTarget, user]);
  const prefetchHref = (href?: string) => {
    if (!href) return;
    preloadRouteByPath(href);
  };
  const getCourseParticuliersHref = (course: string) =>
    `/cours-particuliers-${slugify(course)}`;

  const handleStopImpersonation = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonation();
      navigate("/admin");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Impossible de quitter l'impersonation.";

      window.alert(message);
    } finally {
      setStoppingImpersonation(false);
    }
  };

  useEffect(() => {
    if (canToggleTheme || typeof window === "undefined") return;

    window.localStorage.setItem(ThemeProps.KEY, ThemeProps.DARK);
    document.documentElement.classList.add(ThemeProps.DARK);
    document.documentElement.classList.remove("light");
  }, [canToggleTheme]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.search]);

  return (
    <HeroUINavbar
      isMenuOpen={isMenuOpen}
      maxWidth="xl"
      position="sticky"
      onMenuOpenChange={setIsMenuOpen}
    >
      <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
        <NavbarBrand className="gap-3 max-w-fit">
          <Link
            className="flex justify-start items-center gap-1"
            color="foreground"
            href="/"
            onFocus={() => prefetchHref("/")}
            onMouseEnter={() => prefetchHref("/")}
          >
            <Logo />
            <p className="font-bold text-inherit">Franc-Succès</p>
          </Link>
        </NavbarBrand>
        <div className="ml-2 hidden items-center justify-start gap-4 lg:flex">
          {siteConfig.navItems.map((item) => {
            if (item.label !== "Professeurs") {
              return (
                <NavbarItem key={item.href}>
                  <Link
                    className={clsx(
                      linkStyles({ color: "foreground" }),
                      "inline-flex h-8 items-center leading-none data-[active=true]:font-medium data-[active=true]:text-primary",
                      item.label === "ProfLinker" &&
                        "rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 text-white font-semibold shadow-sm hover:text-white",
                    )}
                    color={
                      item.label === "ProfLinker" ? "primary" : "foreground"
                    }
                    href={item.href}
                    onFocus={() => prefetchHref(item.href)}
                    onMouseEnter={() => prefetchHref(item.href)}
                  >
                    {item.label}
                  </Link>
                </NavbarItem>
              );
            }

            return (
              <div
                key="cours-particuliers-group"
                className="flex items-center gap-4"
              >
                <NavbarItem key={item.href}>
                  <Link
                    className={clsx(
                      linkStyles({ color: "foreground" }),
                      "data-[active=true]:text-primary data-[active=true]:font-medium",
                    )}
                    color="foreground"
                    href={item.href}
                    onFocus={() => prefetchHref(item.href)}
                    onMouseEnter={() => prefetchHref(item.href)}
                  >
                    {item.label}
                  </Link>
                </NavbarItem>
                <NavbarItem>
                  <Dropdown placement="bottom-start">
                    <DropdownTrigger>
                      <Link
                        className={clsx(
                          linkStyles({ color: "foreground" }),
                          "inline-flex h-8 cursor-pointer items-center leading-none data-[active=true]:font-medium data-[active=true]:text-primary",
                        )}
                        color="foreground"
                      >
                        Cours Particuliers
                      </Link>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Cours particuliers"
                      className="dropdown-motion"
                    >
                      {[
                        "Mathématiques",
                        "Physique-Chimie",
                        "Français",
                        "Anglais",
                        "NSI",
                      ].map((course) => (
                        <DropdownItem
                          key={course}
                          href={getCourseParticuliersHref(course)}
                        >
                          Cours particuliers de {course}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </NavbarItem>
              </div>
            );
          })}
        </div>
      </NavbarContent>

      <NavbarContent
        className="hidden sm:flex basis-1/5 sm:basis-full"
        justify="end"
      >
        <NavbarItem className="hidden sm:flex gap-2 items-center">
          {user?.impersonationActive && (
            <Button
              color="warning"
              isLoading={stoppingImpersonation}
              size="sm"
              variant="flat"
              onPress={handleStopImpersonation}
            >
              Quitter l&apos;impersonation
            </Button>
          )}
          {notificationDropdown}
          {canToggleTheme && <ThemeSwitch />}
          {user ? (
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Avatar
                  className="cursor-pointer"
                  name={user.name}
                  size="sm"
                  src={user.avatarUrl || undefined}
                />
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Menu utilisateur"
                className="dropdown-motion"
              >
                <DropdownItem key="dashboard" href="/tableau-de-bord">
                  Tableau de bord
                </DropdownItem>
                <DropdownItem
                  key="logout"
                  className="text-danger"
                  href="/deconnexion"
                >
                  Déconnexion
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          ) : (
            <>
              <Button
                as={Link}
                href={`/connexion?redirect=${encodeURIComponent(redirectTarget)}`}
                variant="flat"
              >
                Connexion
              </Button>
              <Button as={Link} color="primary" href="/inscription">
                Inscription
              </Button>
            </>
          )}
        </NavbarItem>
      </NavbarContent>

      <NavbarContent className="sm:hidden basis-1 pl-4" justify="end">
        {notificationDropdown}
        {canToggleTheme && <ThemeSwitch />}
        <NavbarMenuToggle />
      </NavbarContent>

      <NavbarMenu>
        <div className="mx-4 mt-2 flex flex-col gap-2">
          <NavbarMenuItem>
            <span className="text-sm font-semibold text-default-600">
              Cours Particuliers
            </span>
            <div className="mt-2 flex flex-col gap-2">
              {[
                "Mathématiques",
                "Physique-Chimie",
                "Français",
                "Anglais",
                "NSI",
              ].map((item) => (
                <Link
                  key={item}
                  color="foreground"
                  href={getCourseParticuliersHref(item)}
                  size="lg"
                  onClick={closeMenu}
                  onFocus={() => prefetchHref(getCourseParticuliersHref(item))}
                  onMouseEnter={() =>
                    prefetchHref(getCourseParticuliersHref(item))
                  }
                >
                  Cours particuliers de {item}
                </Link>
              ))}
            </div>
          </NavbarMenuItem>
          {mobileMenuItems.map((item) => (
            <NavbarMenuItem key={`${item.href}-${item.label}`}>
              <Link
                className={clsx(
                  "w-full",
                  item.label === "ProfLinker" &&
                    "rounded-lg bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 px-2 py-1 font-semibold",
                )}
                color={item.color}
                href={item.href}
                size="lg"
                onClick={closeMenu}
                onFocus={() => prefetchHref(item.href)}
                onMouseEnter={() => prefetchHref(item.href)}
              >
                {item.label}
              </Link>
            </NavbarMenuItem>
          ))}
        </div>
      </NavbarMenu>
    </HeroUINavbar>
  );
};
